// No-auth local preview mode. `isMock()` is only ever true when explicitly enabled via
// VITE_MOCK for local dev (see .env.example) — the check is `import.meta.env.DEV &&
// VITE_MOCK==='1'` so a production `vite build` compiles this to a constant `false` and
// dead-code-eliminates every mock branch, same guarantee portal/web/src/mock.ts relies on.
export function isMock(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_MOCK === '1';
}

import type {
  AdminUsage,
  AiArtifact,
  AssistantAnswer,
  AssistantAskRequest,
  AudioPlaybackUrl,
  BudgetState,
  MemberBudget,
  MemberUsage,
  OrgSettings,
  Recording,
  RecordingList,
  Transcript,
  TranscriptVersion,
  TranscriptWords,
  TranscriptWordSegment,
  Folder,
  FolderList,
  CreateFolderRequest,
  UpdateFolderRequest,
  Tag,
  TagList,
  CreateTagRequest,
  UpdateTagRequest,
  UpdateOrgSettingsRequest,
  Vocabulary,
  VocabularyList,
  SearchResultList,
  PageInfo,
  TranscriptExportFormat,
  Share,
  ResourceType,
  PrincipalType,
  PermissionLevel,
  Me,
  ProvisionUserRequest,
  UserList,
  UserSummary,
} from '@scribe/core/types';

const now = new Date().toISOString();

function page(total: number): PageInfo {
  return { page: 1, page_size: 20, total, has_more: false };
}

// A tiny (0.15s, 8kHz, 8-bit mono) *silent* WAV, base64-encoded — not real audio, just
// enough of a valid WAV container for the browser's <audio> element to load and "play"
// (silence) in mock mode. INGEST-3: mock previously returned `url: ''` here, which is
// falsy and made every mock Play button silently no-op instead of rendering a player —
// this placeholder keeps mock mode honest about what Play actually does. Obviously
// non-real: nothing in mock mode has ever recorded audio.
const MOCK_SILENT_WAV_DATA_URL =
  'data:audio/wav;base64,UklGRtQEAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YbAEAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgIA=';

export const mockPlayableAudioUrl: AudioPlaybackUrl = {
  url: MOCK_SILENT_WAV_DATA_URL,
  expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
};

export const mockFolders: Folder[] = [
  {
    id: 'folder-1',
    owner_id: 'user-1',
    parent_id: null,
    name: 'Client Standups',
    created_at: now,
    updated_at: now,
  },
  {
    id: 'folder-2',
    owner_id: 'user-1',
    parent_id: null,
    name: 'Internal',
    created_at: now,
    updated_at: now,
  },
];

export const mockTags: Tag[] = [
  { id: 'tag-1', owner_id: 'user-1', name: 'standup', color: '#1540C0', created_at: now },
  { id: 'tag-2', owner_id: 'user-1', name: 'client-call', color: '#FF3677', created_at: now },
];

export const mockRecordings: Recording[] = [
  {
    id: 'rec-1',
    owner_id: 'user-1',
    folder_id: 'folder-1',
    title: 'Sample Placeholder Sprint Review',
    source: 'upload',
    source_ref: null,
    s3_audio_key: 'audio/rec-1.m4a',
    audio_available: true,
    duration_sec: 1830,
    language: 'en-US',
    status: 'completed',
    pii_redacted: false,
    created_at: now,
    updated_at: now,
  },
  {
    id: 'rec-2',
    owner_id: 'user-1',
    folder_id: null,
    title: 'Sample Placeholder Discovery Call',
    source: 'upload',
    source_ref: null,
    s3_audio_key: 'audio/rec-2.m4a',
    audio_available: true,
    duration_sec: null,
    language: null,
    status: 'transcribing',
    pii_redacted: false,
    created_at: now,
    updated_at: now,
    // TIME-1: the honest elapsed anchor is `transcription_started_at`, not `created_at`
    // (which mock data sets to "now" for every fixture) — a few minutes in the past so
    // `dev:mock` demos a ticking, non-zero elapsed readout.
    transcription_started_at: new Date(Date.now() - 3 * 60_000).toISOString(),
  },
  {
    id: 'rec-3',
    owner_id: 'user-1',
    folder_id: null,
    title: 'Sample Placeholder Mic Capture',
    source: 'upload',
    source_ref: null,
    s3_audio_key: 'audio/rec-3.webm',
    audio_available: true,
    duration_sec: 42,
    language: null,
    status: 'queued',
    pii_redacted: false,
    created_at: now,
    updated_at: now,
    // TIME-1: queued-but-not-started window anchor (see rec-2's transcription_started_at).
    transcription_queued_at: new Date(Date.now() - 90_000).toISOString(),
  },
  {
    // SHARE-4: the one fixture NOT owned by `user-1` (the mock signed-in identity — see
    // `mockDirectory`'s comment below), so `dev:mock`'s "Shared with me" view is non-empty
    // and the owned/all views have something to show a `Shared` chip on. In a real backend
    // this row would only appear via an actual `shares` row visible to the caller — mock
    // mode has no shares-aware list_recordings, so `scope="shared"` below just filters on
    // `owner_id !== 'user-1'` directly (see the `mock.listRecordings` comment).
    id: 'rec-4',
    owner_id: 'user-2',
    folder_id: null,
    title: 'Sample Placeholder Teammate Sync',
    source: 'upload',
    source_ref: null,
    s3_audio_key: 'audio/rec-4.m4a',
    audio_available: true,
    duration_sec: 900,
    language: 'en-US',
    status: 'completed',
    pii_redacted: false,
    created_at: now,
    updated_at: now,
  },
  // ONDEMAND-1: two DEFERRED recordings (audio stored, transcription waiting for someone to
  // want it — `status: 'uploaded'` + `audio_available` + `transcription_deferred_at`) and one
  // imported, already-transcribed recording with no summary yet, so `dev:mock` demos
  // transcribe-on-demand (see lib/mockOndemand.ts). Appended last and folder/tag-free so the
  // existing fixtures' order and folder/tag filter results are unchanged.
  {
    id: 'rec-5',
    owner_id: 'user-1',
    folder_id: null,
    title: 'Sample Placeholder Weekly Planning',
    source: 'upload',
    source_ref: null,
    s3_audio_key: 'audio/rec-5.webm',
    audio_available: true,
    duration_sec: null,
    language: null,
    status: 'uploaded',
    pii_redacted: false,
    created_at: now,
    updated_at: now,
    transcription_deferred_at: now,
    estimated_duration_sec: 1_500,
  },
  {
    id: 'rec-6',
    owner_id: 'user-1',
    folder_id: null,
    title: 'Sample Placeholder Vendor Check-in',
    source: 'upload',
    source_ref: null,
    s3_audio_key: 'audio/rec-6.webm',
    audio_available: true,
    duration_sec: null,
    language: null,
    status: 'uploaded',
    pii_redacted: false,
    created_at: now,
    updated_at: now,
    transcription_deferred_at: now,
    estimated_duration_sec: 720,
  },
  {
    id: 'rec-7',
    owner_id: 'user-1',
    folder_id: null,
    title: 'Sample Placeholder Imported Retro',
    source: 'import',
    source_ref: null,
    s3_audio_key: 'audio/rec-7.m4a',
    audio_available: true,
    duration_sec: 2_400,
    language: 'en-US',
    status: 'completed',
    pii_redacted: false,
    created_at: now,
    updated_at: now,
  },
];

// AI-3: canned `kind: 'summary'` artifacts for the seeded `completed` recordings — no real
// names/PII, same placeholder discipline as the rest of this file. Recordings not listed here
// (not completed, or completed with no summary yet) return `[]` from `listRecordingArtifacts`.
export const mockSummaryArtifacts = new Map<string, AiArtifact[]>([
  [
    'rec-1',
    [
      {
        id: 'artifact-summary-rec-1',
        recording_id: 'rec-1',
        kind: 'summary',
        content: {
          summary: 'The team reviewed sprint progress, flagged a placeholder blocker, and agreed on next steps.',
          title: 'Sample Placeholder Sprint Review',
          key_points: [
            'Sprint scope is on track for the placeholder milestone.',
            'One placeholder blocker needs a follow-up owner.',
            'Next review is scheduled for the following placeholder sprint.',
          ],
          // Owners mix raw diarization labels (mapped to display names via
          // `mockTranscript.speakers` in the Action items panel) with a free-text name the
          // model heard, and dues mix ISO dates, free text, and none — mirrors real output.
          action_items: [
            { item: 'Assign a follow-up owner for the placeholder blocker', owners: ['spk_0'], due: '2026-07-15' },
            { item: 'Share the updated placeholder milestone plan with the team', owners: ['spk_1'], due: 'next sprint' },
            { item: 'Schedule the next sprint review', owners: ['spk_0', 'Placeholder Teammate C'], due: null },
            { item: 'Draft release notes for the placeholder feature', owners: [], due: null },
          ],
          topics: ['sprint review', 'placeholder blocker'],
        },
        model_id: 'mock-summarizer',
        created_at: now,
      },
    ],
  ],
  [
    'rec-4',
    [
      {
        id: 'artifact-summary-rec-4',
        recording_id: 'rec-4',
        kind: 'summary',
        content: {
          summary: 'A brief teammate sync covering placeholder status updates across two workstreams.',
          title: 'Sample Placeholder Teammate Sync',
          key_points: [
            'Workstream A is unblocked and moving forward.',
            'Workstream B is waiting on a placeholder dependency.',
          ],
          action_items: [],
          topics: ['status sync'],
        },
        model_id: 'mock-summarizer',
        created_at: now,
      },
    ],
  ],
]);

export const mockTranscript: Transcript = {
  id: 'transcript-1',
  recording_id: 'rec-1',
  engine: 'aws-transcribe',
  engine_version: '1.0',
  kind: 'platform',
  vocab_name: null,
  vocab_version: null,
  word_count: 128,
  created_at: now,
  speakers: [
    // SHARE-6: explicit `resolved_user_id: null` (rather than leaving it undefined) so the
    // "Link person" affordance has a concrete not-linked speaker to render against in
    // `dev:mock`.
    {
      id: 'spk-a',
      recording_id: 'rec-1',
      label: 'spk_0',
      display_name: 'Placeholder Speaker A',
      resolved_user_id: null,
    },
    {
      id: 'spk-b',
      recording_id: 'rec-1',
      label: 'spk_1',
      display_name: 'Placeholder Speaker B',
      resolved_user_id: null,
    },
  ],
  segments: [
    {
      id: 'seg-1',
      transcript_id: 'transcript-1',
      speaker_label: 'spk_0',
      start_ms: 0,
      end_ms: 4200,
      text: 'Thanks everyone for joining, let’s get started with the sprint review.',
    },
    {
      id: 'seg-2',
      transcript_id: 'transcript-1',
      speaker_label: 'spk_1',
      start_ms: 4300,
      end_ms: 9100,
      text: 'Sounds good — I’ll walk through what shipped this sprint first.',
    },
    {
      id: 'seg-3',
      transcript_id: 'transcript-1',
      speaker_label: 'spk_0',
      start_ms: 9200,
      end_ms: 15000,
      text: 'Great, and after that let’s cover the upload queue work and any blockers.',
    },
  ],
};

/** REPLAY-2: derives word-level timings for `dev:mock` by evenly splitting each mock
 * segment's text on spaces across its start_ms..end_ms range (same derivation idea as
 * `pipeline/transcriber.py::_build_canned_transcript_json`). Joining each segment's word
 * texts with single spaces trivially reproduces `segment.text`, so mock mode exercises the
 * same aligned-word rendering path a real backend with raw JSON would. */
function deriveMockWords(): TranscriptWords {
  const segments: TranscriptWordSegment[] = mockTranscript.segments.map((seg) => {
    const words = seg.text.split(' ');
    const span = seg.end_ms - seg.start_ms;
    const step = span / Math.max(words.length, 1);
    return {
      segment_id: seg.id,
      words: words.map((text, i) => ({
        start_ms: Math.round(seg.start_ms + i * step),
        end_ms: Math.round(seg.start_ms + (i + 1) * step),
        text,
      })),
    };
  });
  return { transcript_id: mockTranscript.id, words_available: true, segments };
}

export const mockVocabularies: Vocabulary[] = [
  {
    id: 'vocab-1',
    owner_id: 'user-1',
    name: 'ProductTerms',
    language: 'en-US',
    published_version: 1,
    // §2.6 step 3: the picker only offers vocabularies AWS holds READY, so a mock without this
    // would render as "not registered" and offer nothing — mock mode would misrepresent the
    // working state of the feature.
    engine_state: 'READY',
    terms: [
      { id: 'term-1', phrase: 'Umbrage', display_as: 'Umbrage' },
      { id: 'term-2', phrase: 'Five-Nine', display_as: 'Five9' },
    ],
    created_at: now,
    updated_at: now,
  },
];

// Shares are the one piece of mock state that's actually mutated at runtime (create/list/
// revoke round-trip through this map) rather than served from a static fixture — there's no
// `GET /shares` (list) operation in openapi/scribe.yaml yet (only createShare/deleteShare), so
// mock mode is the only place "list existing shares" can be demonstrated end-to-end. Keyed by
// `${resourceType}:${resourceId}` so different dialogs don't see each other's shares.
const mockShares = new Map<string, Share[]>();
let mockShareSeq = 0;

function shareKey(resourceType: ResourceType, resourceId: string): string {
  return `${resourceType}:${resourceId}`;
}

/** Test-only: clears mock share state so tests don't leak shares across cases. */
export function resetMockShares(): void {
  mockShares.clear();
  mockShareSeq = 0;
}

// Which tags a recording carries is likewise mutated at runtime in mock mode only.
// `openapi/scribe.yaml` has no update-recording operation (only `create`/`get`/`delete` —
// see docs/development-plan.md task W6), so `Recording` itself carries no `tags` field and
// there is no real endpoint to move a recording between folders or change its tags after
// creation. RecordingDetail's "folder & tags" editor therefore only renders in mock mode
// (same rationale, same pattern as `mockShares` above); a real/local-auth backend gets an
// honest "not available yet" notice instead of a call that would 404.
const mockRecordingTagIds = new Map<string, string[]>([['rec-1', ['tag-1']]]);

/** Test-only: resets mock recording folder/tag assignments so tests don't leak state. */
export function resetMockRecordingOrganize(): void {
  for (const r of mockRecordings) {
    const original = INITIAL_RECORDING_FOLDER_IDS.get(r.id);
    r.folder_id = original === undefined ? null : original;
  }
  mockRecordingTagIds.clear();
  mockRecordingTagIds.set('rec-1', ['tag-1']);
}

const INITIAL_RECORDING_FOLDER_IDS = new Map<string, string | null>(
  mockRecordings.map((r) => [r.id, r.folder_id]),
);

let mockFolderSeq = mockFolders.length;
let mockTagSeq = mockTags.length;

export const mock = {
  // SHARE-4: mock mode has no shares store to consult (unlike the real repository's
  // `_visible_resource_ids`), so `scope="shared"` approximates it the same way the web
  // screen's indicator does elsewhere in mock mode — `owner_id !== 'user-1'` (the mock
  // signed-in identity, see `mockDirectory`). `'owned' | undefined | 'all'` all return
  // everything, matching the real API's default.
  // SHARE-5: `folderId`/`tag` mirror the real API's `folder_id`/`tag` list params so the
  // rail/dropdown filters actually filter something in `dev:mock`, not just against a real
  // backend — `folder_id` matches `Recording.folder_id` directly; `tag` resolves to a tag
  // id via `mockTags` and checks `mockRecordingTagIds` (the same per-recording tag store
  // RecordingDetail's mock-only organize editor mutates).
  listRecordings: (scope?: 'owned' | 'shared' | 'all', folderId?: string, tag?: string): RecordingList => {
    // Always a copy (ONDEMAND-1): lib/mockOndemand.ts swaps fixture rows in place, and handing
    // React Query the live array would make its structural sharing see "no change".
    let items = scope === 'shared' ? mockRecordings.filter((r) => r.owner_id !== 'user-1') : [...mockRecordings];
    if (folderId) items = items.filter((r) => r.folder_id === folderId);
    if (tag) {
      const tagId = mockTags.find((t) => t.name === tag)?.id;
      items = items.filter((r) => tagId != null && (mockRecordingTagIds.get(r.id) ?? []).includes(tagId));
    }
    return { items, page_info: page(items.length) };
  },
  getRecording: (id: string): Recording | undefined => mockRecordings.find((r) => r.id === id),
  getTranscript: (): Transcript => mockTranscript,
  // TVER-3: mock mode intentionally keeps a single (authoritative) version — the switcher
  // stays hidden in `dev:mock`; multi-version behavior is covered by hook-mocked component
  // tests instead of mock-db plumbing.
  // REPLAY-2: `has_raw_json: true` (was `false` under TVER-3, before word-replay existed) —
  // `dev:mock` needs this so `useTranscriptWords`'s gate actually fires and karaoke demos
  // with zero backend, matching `deriveMockWords`' `words_available: true`.
  listTranscriptVersions: (): TranscriptVersion[] => [
    {
      id: mockTranscript.id,
      recording_id: mockTranscript.recording_id,
      engine: mockTranscript.engine,
      engine_version: mockTranscript.engine_version ?? null,
      kind: 'platform',
      derived_from: null,
      is_authoritative: true,
      vocab_name: null,
      vocab_version: null,
      word_count: mockTranscript.word_count,
      has_raw_json: true,
      created_at: mockTranscript.created_at,
    },
  ],
  getTranscriptWords: (): TranscriptWords => deriveMockWords(),
  listFolders: (): FolderList => ({ items: mockFolders, page_info: page(mockFolders.length) }),
  // Folders (and tags, below) mutate the shared fixture array in place — like `mockShares`,
  // so a create/rename/delete round-trips through the same list a subsequent refetch reads,
  // rather than reverting on the next `invalidateQueries`.
  createFolder: (input: CreateFolderRequest): Folder => {
    const f: Folder = {
      id: `folder-${++mockFolderSeq}`,
      owner_id: mockUser.profile.oid,
      parent_id: input.parent_id ?? null,
      name: input.name,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mockFolders.push(f);
    return f;
  },
  updateFolder: (id: string, body: UpdateFolderRequest): Folder | undefined => {
    const f = mockFolders.find((folder) => folder.id === id);
    if (!f) return undefined;
    if (body.name !== undefined) f.name = body.name;
    if (body.parent_id !== undefined) f.parent_id = body.parent_id;
    f.updated_at = new Date().toISOString();
    return f;
  },
  deleteFolder: (id: string): void => {
    const idx = mockFolders.findIndex((f) => f.id === id);
    if (idx >= 0) mockFolders.splice(idx, 1);
  },
  listTags: (): TagList => ({ items: mockTags, page_info: page(mockTags.length) }),
  createTag: (input: CreateTagRequest): Tag => {
    const t: Tag = {
      id: `tag-${++mockTagSeq}`,
      owner_id: mockUser.profile.oid,
      name: input.name,
      color: input.color ?? null,
      created_at: new Date().toISOString(),
    };
    mockTags.push(t);
    return t;
  },
  updateTag: (id: string, body: UpdateTagRequest): Tag | undefined => {
    const t = mockTags.find((tag) => tag.id === id);
    if (!t) return undefined;
    if (body.name !== undefined) t.name = body.name;
    if (body.color !== undefined) t.color = body.color;
    return t;
  },
  deleteTag: (id: string): void => {
    const idx = mockTags.findIndex((t) => t.id === id);
    if (idx >= 0) mockTags.splice(idx, 1);
  },
  /** Moves a recording to `folderId` (or out of any folder, if null) — see the
   * `mockRecordingTagIds` comment above for why this only exists in mock mode. */
  moveRecordingToFolder: (id: string, folderId: string | null): Recording | undefined => {
    const r = mockRecordings.find((rec) => rec.id === id);
    if (!r) return undefined;
    r.folder_id = folderId;
    r.updated_at = new Date().toISOString();
    return r;
  },
  getRecordingTagIds: (id: string): string[] => mockRecordingTagIds.get(id) ?? [],
  setRecordingTagIds: (id: string, tagIds: string[]): string[] => {
    mockRecordingTagIds.set(id, tagIds);
    return tagIds;
  },
  /** Full tag objects for a recording's seeded tag ids (WEBUI-1 #4's list-card chips) — `[]`
   * if the recording has none. */
  getRecordingTags: (id: string): Tag[] => {
    const ids = new Set(mockRecordingTagIds.get(id) ?? []);
    return mockTags.filter((t) => ids.has(t.id));
  },
  /** AI-3: AI artifacts derived from a recording, newest first, filtered by `kind` when given.
   * Only the seeded `completed` fixtures have a canned `summary` artifact; everything else
   * returns `[]` (mirrors the real "generated asynchronously after completion" behavior). */
  listRecordingArtifacts: (id: string, kind?: string): AiArtifact[] => {
    const items = mockSummaryArtifacts.get(id) ?? [];
    return kind ? items.filter((a) => a.kind === kind) : items;
  },
  /** Placeholder presigned playback URL for mock mode's inline Play preview (WEBUI-1 #1)
   * and the detail view's player — there's no real S3 object behind it, so `mockPlayableAudioUrl`
   * is a small embedded silent WAV rather than a real presigned S3 URL, but it's a
   * genuinely playable clip (non-empty `url`) so the `<audio>` element renders and plays
   * instead of silently no-op'ing (INGEST-3). */
  getRecordingAudioUrl: (_id: string): AudioPlaybackUrl => mockPlayableAudioUrl,
  listVocabularies: (): VocabularyList => ({
    items: mockVocabularies,
    page_info: page(mockVocabularies.length),
  }),
  /** Mock delete is a no-op returning void: mock mode has no admin role to check, and mutating
   *  the module-level fixture would leak between tests. */
  deleteVocabulary: (_id: string): void => undefined,
  exportTranscript: (format: TranscriptExportFormat): string => {
    const speakerName = (label: string) =>
      mockTranscript.speakers?.find((s) => s.label === label)?.display_name ?? label;
    if (format === 'vtt') {
      const toVttTs = (ms: number) => {
        const totalMs = Math.max(0, Math.floor(ms));
        const h = Math.floor(totalMs / 3_600_000);
        const m = Math.floor((totalMs % 3_600_000) / 60_000);
        const s = Math.floor((totalMs % 60_000) / 1000);
        const msec = totalMs % 1000;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(msec).padStart(3, '0')}`;
      };
      const cues = mockTranscript.segments
        .map(
          (s) =>
            `${toVttTs(s.start_ms)} --> ${toVttTs(s.end_ms)}\n${speakerName(s.speaker_label)}: ${s.text}`,
        )
        .join('\n\n');
      return `WEBVTT\n\n${cues}\n`;
    }
    if (format === 'md') {
      return mockTranscript.segments
        .map((s) => `**${speakerName(s.speaker_label)}:** ${s.text}`)
        .join('\n\n');
    }
    return mockTranscript.segments.map((s) => `${speakerName(s.speaker_label)}: ${s.text}`).join('\n\n');
  },
  /** Assistant Q&A in mock mode: a deterministic, clearly-placeholder answer citing the
   * seeded transcript's segments (scoped to `recording_id` when it's the transcript's
   * recording; any other scoped id gets the no-transcript answer), plus canned follow-ups.
   * Never fabricates meeting content beyond the existing placeholder fixtures. */
  askAssistant: (body: AssistantAskRequest): AssistantAnswer => {
    if (body.recording_id && body.recording_id !== mockTranscript.recording_id) {
      return {
        answer: "This recording doesn't have a transcript yet, so there's nothing to answer from.",
        citations: [],
        suggestions: [],
      };
    }
    const title = mockRecordings.find((r) => r.id === mockTranscript.recording_id)?.title ?? null;
    const label = (ms: number) => {
      const s = Math.floor(ms / 1000);
      return `${title ?? 'Untitled recording'} · ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    };
    return {
      answer:
        'Mock answer (no backend): the placeholder sprint review opened with what shipped, then moved on to the upload queue work and blockers.',
      citations: mockTranscript.segments.slice(1, 3).map((seg) => ({
        recording_id: mockTranscript.recording_id,
        segment_id: seg.id,
        start_ms: seg.start_ms,
        speaker_label: seg.speaker_label,
        recording_title: title,
        label: label(seg.start_ms),
      })),
      suggestions: ['What blockers were raised?', 'Who owns the follow-ups?'],
    };
  },
  search: (q: string): SearchResultList => ({
    items: q
      ? [
          {
            recording_id: 'rec-1',
            transcript_id: 'transcript-1',
            segment_id: 'seg-1',
            recording_title: mockRecordings[0]!.title,
            snippet: `…let’s get started with the ${q}…`,
            speaker_label: 'spk_0',
            start_ms: 0,
            score: 1.0,
          },
        ]
      : [],
    page_info: page(q ? 1 : 0),
  }),
  listShares: (resourceType: ResourceType, resourceId: string): Share[] =>
    mockShares.get(shareKey(resourceType, resourceId)) ?? [],
  createShare: (
    resourceType: ResourceType,
    resourceId: string,
    input: { principalType: PrincipalType; principalId: string; perm: PermissionLevel },
  ): Share => {
    const key = shareKey(resourceType, resourceId);
    const existing = mockShares.get(key) ?? [];
    // Re-sharing with the same principal updates the permission rather than duplicating the
    // row — mirrors what a real upsert-on-conflict would do server-side.
    const withoutPrincipal = existing.filter(
      (s) => !(s.principal_type === input.principalType && s.principal_id === input.principalId),
    );
    const share: Share = {
      id: `mock-share-${++mockShareSeq}`,
      resource_type: resourceType,
      resource_id: resourceId,
      principal_type: input.principalType,
      principal_id: input.principalId,
      perm: input.perm,
      created_by: mockUser.profile.oid,
      created_at: new Date().toISOString(),
    };
    mockShares.set(key, [...withoutPrincipal, share]);
    return share;
  },
  deleteShare: (
    resourceType: ResourceType,
    resourceId: string,
    input: { principalType: PrincipalType; principalId: string },
  ): void => {
    const key = shareKey(resourceType, resourceId);
    const existing = mockShares.get(key) ?? [];
    mockShares.set(
      key,
      existing.filter(
        (s) => !(s.principal_type === input.principalType && s.principal_id === input.principalId),
      ),
    );
  },
  // SHARE-2: directory lookups backing useMe/useUsersByIds/useUserSearch in `dev:mock`. See
  // `mockDirectory`'s comment below for the fixture data itself.
  /** The mock signed-in user's own directory entry (`user-1`, same identity ApiContext shows
   * in mock mode via `mockUser.profile`) plus the admin flag, so `dev:mock` can exercise
   * ADMIN-5's nav-gated screen. */
  getMe: (): Me => ({ ...mockDirectory[0]!, is_admin: true }),
  /** Batch-resolves platform user ids to directory entries (SHARE-2's `useUsersByIds`) —
   * unknown ids are simply absent from the result, same as the real `GET /users?ids=`. */
  listUsersByIds: (ids: string[]): UserList => {
    const idSet = new Set(ids);
    return { items: mockDirectory.filter((u) => idSet.has(u.id)) };
  },
  /** Directory typeahead (SHARE-3/SHARE-6's `useUserSearch`) — mirrors the backend's prefix
   * semantics (`repository_fake.py`'s `search_users`): email prefix, display-name prefix, or
   * any individual name-word prefix, all case-insensitive. */
  searchUsers: (q: string): UserList => {
    const needle = q.trim().toLowerCase();
    if (!needle) return { items: [] };
    const matches = mockDirectory.filter((u) => {
      if (u.email.toLowerCase().startsWith(needle)) return true;
      if (u.display_name.toLowerCase().startsWith(needle)) return true;
      return u.display_name
        .toLowerCase()
        .split(/\s+/)
        .some((word) => word.startsWith(needle));
    });
    matches.sort((a, b) => a.display_name.localeCompare(b.display_name) || a.email.localeCompare(b.email));
    return { items: matches };
  },
  // SHARE-7: the mock stand-in for `createGraphDirectorySource` — same prefix semantics as
  // `searchUsers` (email/name/any-word, case-insensitive), over `mockExternalDirectory`
  // instead of `mockDirectory`, so `dev:mock`'s share picker can demo/test the merged UX
  // (platform + "hasn't signed in yet" rows) with no real Graph/consent dependency.
  searchExternalDirectory: (q: string): Promise<ProvisionUserRequest[]> => {
    const needle = q.trim().toLowerCase();
    if (!needle) return Promise.resolve([]);
    const matches = mockExternalDirectory.filter((p) => {
      if (p.email.toLowerCase().startsWith(needle)) return true;
      if (p.display_name.toLowerCase().startsWith(needle)) return true;
      return p.display_name
        .toLowerCase()
        .split(/\s+/)
        .some((word) => word.startsWith(needle));
    });
    matches.sort((a, b) => a.display_name.localeCompare(b.display_name) || a.email.localeCompare(b.email));
    return Promise.resolve(matches);
  },
  /** Mock stand-in for `POST /users/provision`: idempotent get-or-create into `mockDirectory`
   *  keyed on `entra_oid`, mirroring the backend's semantics (incl. the no-overwrite rule on
   *  an existing oid) so a provisioned person immediately resolves via `mock.listUsersByIds`
   *  the same way a real provisioned row resolves through `GET /users?ids=`. */
  provisionUser: (body: ProvisionUserRequest): UserSummary => {
    const existing = mockDirectory.find((u) => u.entra_oid === body.entra_oid);
    if (existing) return existing;
    const created: UserSummary = {
      id: `user-${++mockUserSeq}`,
      entra_oid: body.entra_oid,
      email: body.email,
      display_name: body.display_name,
    };
    mockDirectory.push(created);
    return created;
  },
  // ADMIN-5: org usage rollup + budget/settings storage for `dev:mock` — see the fixture
  // data + `computeBudgetState`/`effectiveMemberBudget` helpers below (declared after
  // `mockDirectory`, which these read from) for the actual numbers. `budget_state` is
  // recomputed at read time from current settings/overrides, same as the real
  // `GET /admin/usage` handler (`backend/src/scribe/api/routers/admin.py`) — so a
  // `setMemberBudget`/`updateOrgSettings` round-trip is honestly reflected on the next
  // `getAdminUsage` call rather than a stale cached chip.
  getAdminUsage: (month?: string): AdminUsage => {
    const normalizedMonth = month || currentUtcMonth();
    let totalRecordings = 0;
    let totalBatch = 0;
    let totalStreaming = 0;
    let totalStorage = 0;
    let totalAiIn = 0;
    let totalAiOut = 0;
    let totalCost = 0;
    const members: MemberUsage[] = mockAdminMembersBase.map((m) => {
      const budget = effectiveMemberBudget(m.user_id);
      totalRecordings += m.recordings_count;
      totalBatch += m.batch_seconds;
      totalStreaming += m.streaming_seconds;
      totalStorage += m.storage_bytes;
      totalAiIn += m.ai_tokens_in;
      totalAiOut += m.ai_tokens_out;
      totalCost += m.estimated_cost_usd;
      return {
        ...m,
        monthly_budget_usd: budget,
        budget_state: computeBudgetState(m.estimated_cost_usd, budget, mockOrgSettings.warn_threshold_pct),
      };
    });
    const roundedTotalCost = Math.round(totalCost * 100) / 100;
    return {
      month: normalizedMonth,
      members,
      totals: {
        recordings_count: totalRecordings,
        batch_seconds: totalBatch,
        streaming_seconds: totalStreaming,
        storage_bytes: totalStorage,
        ai_tokens_in: totalAiIn,
        ai_tokens_out: totalAiOut,
        estimated_cost_usd: roundedTotalCost,
        org_monthly_budget_usd: mockOrgSettings.org_monthly_budget_usd,
        budget_state: computeBudgetState(
          roundedTotalCost,
          mockOrgSettings.org_monthly_budget_usd,
          mockOrgSettings.warn_threshold_pct,
        ),
      },
      // ONDEMAND-1: canned savings rollup (the real one is computed server-side).
      on_demand: {
        deferred_count: 18,
        never_transcribed_count: 11,
        never_transcribed_seconds: 26_400,
        transcribed_on_access_count: 7,
        estimated_savings_usd: 10.56,
        summaries_missing_count: 3,
        summaries_on_access_count: 2,
      },
    };
  },
  getOrgSettings: (): OrgSettings => ({ ...mockOrgSettings }),
  updateOrgSettings: (body: UpdateOrgSettingsRequest): OrgSettings => {
    if (body.org_monthly_budget_usd !== undefined) {
      mockOrgSettings.org_monthly_budget_usd = body.org_monthly_budget_usd;
    }
    if (body.default_member_monthly_budget_usd !== undefined) {
      mockOrgSettings.default_member_monthly_budget_usd = body.default_member_monthly_budget_usd;
    }
    if (body.enforcement !== undefined) mockOrgSettings.enforcement = body.enforcement;
    if (body.warn_threshold_pct !== undefined) mockOrgSettings.warn_threshold_pct = body.warn_threshold_pct;
    if (body.transcription_policy_default !== undefined) {
      mockOrgSettings.transcription_policy_default = body.transcription_policy_default;
    }
    mockOrgSettings.updated_at = new Date().toISOString();
    return { ...mockOrgSettings };
  },
  /** `monthlyBudgetUsd: null` stores an explicit-uncapped override (mirrors the real
   * `PUT /admin/members/{id}/budget` — see `effectiveMemberBudget` below). */
  setMemberBudget: (userId: string, monthlyBudgetUsd: number | null): MemberBudget => {
    mockMemberBudgetOverrides.set(userId, monthlyBudgetUsd);
    return { user_id: userId, monthly_budget_usd: monthlyBudgetUsd, updated_at: new Date().toISOString() };
  },
};

export const mockUser = {
  profile: {
    // 'oid' is native on Entra v2 access tokens — no custom-attribute fallback needed
    // (unlike the earlier Cognito-federated setup). Mock mode never hits a real backend,
    // so this is display-only fixture data.
    oid: 'mock-oid-00000000-0000-0000-0000-000000000000',
    email: 'mock.user@example.invalid',
    name: 'Mock User',
  },
};

/**
 * SHARE-2 directory fixtures for `useMe`/`useUsersByIds`/`useUserSearch` in `dev:mock`.
 * Obviously-fake placeholder names/emails throughout (org policy: no real names/emails). The
 * first entry is `user-1`, matching `owner_id: 'user-1'` on the `mockRecordings`/`mockFolders`/
 * `mockTags` fixtures above, and is deliberately built from `mockUser.profile` rather than a
 * separate placeholder identity: in mock mode the signed-in user shown in the app shell (see
 * `ApiContext.tsx`'s `isMock()` branch) IS `user-1`, the owner of every fixture — resolved
 * through the exact same directory lookup any other owner id goes through — and `mock.getMe()`
 * returns this same entry plus `is_admin: true` so `dev:mock` can exercise ADMIN-5's
 * nav-gated screen.
 * (Amended 2026-08-04 — SHARE-7: `entra_oid` is a required field on `UserSummary`/`Me`; these
 * `oid-user-N` values are chosen to not collide with SHARE-7's external-directory `oid-ext-N`
 * fixtures — see docs/specs/share-7.md §5.5.)
 */
export const mockDirectory: UserSummary[] = [
  {
    id: 'user-1',
    entra_oid: 'oid-user-1',
    display_name: mockUser.profile.name,
    email: mockUser.profile.email,
  },
  {
    id: 'user-2',
    entra_oid: 'oid-user-2',
    display_name: 'Sample Teammate',
    email: 'sample.teammate@example.invalid',
  },
  {
    id: 'user-3',
    entra_oid: 'oid-user-3',
    display_name: 'Fixture Colleague',
    email: 'fixture.colleague@example.invalid',
  },
  {
    id: 'user-4',
    entra_oid: 'oid-user-4',
    display_name: 'Demo Reviewer',
    email: 'demo.reviewer@example.invalid',
  },
];

let mockUserSeq = mockDirectory.length;

/**
 * SHARE-7 mock external-directory fixture: 2–3 obviously-fake org members who have never
 * signed in to Scribe (the `dev:mock` stand-in for a Microsoft Graph people-search result).
 * Oids deliberately do NOT collide with `mockDirectory`'s `oid-user-N` values above.
 */
export const mockExternalDirectory: ProvisionUserRequest[] = [
  {
    entra_oid: 'oid-ext-1',
    display_name: 'Unsigned Placeholder',
    email: 'unsigned.placeholder@example.invalid',
  },
  {
    entra_oid: 'oid-ext-2',
    display_name: 'Pending Colleague',
    email: 'pending.colleague@example.invalid',
  },
  {
    entra_oid: 'oid-ext-3',
    display_name: 'Never Signedin',
    email: 'never.signedin@example.invalid',
  },
];

// ── ADMIN-5: org usage rollup + budget/settings fixtures ───────────────────────────

/** `YYYY-MM`, UTC — mirrors the real `GET /admin/usage`'s default-to-current-month
 * behavior (`backend/src/scribe/api/routers/admin.py`'s `_month_window`). */
function currentUtcMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Mirrors `backend/src/scribe/metering/enforcement.py`'s `budget_state` exactly, so
 * `dev:mock`'s chips behave the same way a real backend's would as settings/overrides
 * change: no budget (null) is always 'ok'; at/past the cap is 'exceeded'; at/past
 * `warn_threshold_pct`% of the cap is 'warn'; otherwise 'ok'. */
function computeBudgetState(spend: number, budget: number | null, warnThresholdPct: number): BudgetState {
  if (budget == null) return 'ok';
  if (spend >= budget) return 'exceeded';
  if (spend >= budget * (warnThresholdPct / 100)) return 'warn';
  return 'ok';
}

/** Singleton `org_settings` fixture (mirrors the real `org_settings` row) — mutated in
 * place by `mock.updateOrgSettings` so `dev:mock` round-trips a settings save. Default
 * member budget starts uncapped (null) so the seeded zero-usage member (no override
 * either) demonstrates the "—"/uncapped display path. */
const mockOrgSettings: OrgSettings = {
  org_monthly_budget_usd: 500,
  default_member_monthly_budget_usd: null,
  enforcement: 'warn',
  warn_threshold_pct: 80,
  updated_at: now,
  // ONDEMAND-1: non-null = the feature is on (mock mode demos it enabled). Ships 'immediate'
  // (product decision 2026-09-30) — an admin opts the org into 'on_access'.
  transcription_policy_default: 'immediate',
};

/** Per-member budget overrides (mirrors the real `user_budgets` table) — a present entry
 * (including an explicit `null`) always wins over `mockOrgSettings.default_member_monthly_
 * budget_usd`; an absent entry falls back to the org default. Seeded with explicit $50 caps
 * for the three non-zero-usage members below; the zero-usage member has no entry, so it
 * inherits the (null/uncapped) org default. */
const mockMemberBudgetOverrides = new Map<string, number | null>([
  ['user-1', 50],
  ['user-2', 50],
  ['user-3', 50],
]);

function effectiveMemberBudget(userId: string): number | null {
  return mockMemberBudgetOverrides.has(userId)
    ? mockMemberBudgetOverrides.get(userId)!
    : mockOrgSettings.default_member_monthly_budget_usd;
}

/** Base per-member usage/cost fixture (everything EXCEPT `monthly_budget_usd`/
 * `budget_state`, which `mock.getAdminUsage` derives at read time from the current
 * settings/overrides above) — one `ok`, one `warn`, one `exceeded` (all $50 budgets,
 * varied spend), and one zero-usage/uncapped member, per docs/specs/admin-5.md §3.
 * Reuses `mockDirectory`'s obviously-fake identities rather than inventing new ones — no
 * real employee names/emails, per org policy. */
const mockAdminMembersBase: Omit<MemberUsage, 'monthly_budget_usd' | 'budget_state'>[] = [
  {
    user_id: 'user-1',
    display_name: mockDirectory[0]!.display_name,
    email: mockDirectory[0]!.email,
    recordings_count: 12,
    batch_seconds: 7_200,
    streaming_seconds: 600,
    storage_bytes: 2 * 1024 ** 3,
    ai_tokens_in: 15_000,
    ai_tokens_out: 5_000,
    estimated_cost_usd: 4.5,
  },
  {
    user_id: 'user-2',
    display_name: mockDirectory[1]!.display_name,
    email: mockDirectory[1]!.email,
    recordings_count: 40,
    batch_seconds: 61_200,
    streaming_seconds: 3_600,
    storage_bytes: 9 * 1024 ** 3,
    ai_tokens_in: 120_000,
    ai_tokens_out: 40_000,
    estimated_cost_usd: 42,
  },
  {
    user_id: 'user-3',
    display_name: mockDirectory[2]!.display_name,
    email: mockDirectory[2]!.email,
    recordings_count: 58,
    batch_seconds: 90_000,
    streaming_seconds: 7_200,
    storage_bytes: 14 * 1024 ** 3,
    ai_tokens_in: 200_000,
    ai_tokens_out: 65_000,
    estimated_cost_usd: 60,
  },
  {
    user_id: 'user-4',
    display_name: mockDirectory[3]!.display_name,
    email: mockDirectory[3]!.email,
    recordings_count: 0,
    batch_seconds: 0,
    streaming_seconds: 0,
    storage_bytes: 0,
    ai_tokens_in: 0,
    ai_tokens_out: 0,
    estimated_cost_usd: 0,
  },
];

/** Test-only: resets ADMIN-5's mock settings/overrides so tests don't leak state. */
export function resetMockAdmin(): void {
  mockOrgSettings.org_monthly_budget_usd = 500;
  mockOrgSettings.default_member_monthly_budget_usd = null;
  mockOrgSettings.enforcement = 'warn';
  mockOrgSettings.warn_threshold_pct = 80;
  mockOrgSettings.updated_at = now;
  mockOrgSettings.transcription_policy_default = 'immediate';
  mockMemberBudgetOverrides.clear();
  mockMemberBudgetOverrides.set('user-1', 50);
  mockMemberBudgetOverrides.set('user-2', 50);
  mockMemberBudgetOverrides.set('user-3', 50);
}
